import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  LeadDistributionScope,
  UnassignedLeadItem,
} from "@shared/leads/leadDistribution";
import { canDistributeLeads } from "@shared/leads/leadDistribution";

import { apiGet, apiPost } from "@/lib/api/client";
import { useAuth } from "@/lib/auth/AuthContext";
import { queryKeys } from "@/lib/query/keys";

// ---------------------------------------------------------------------------
// useUnassignedLeads — die Daten der «Wer uebernimmt?»-Kachel (LEAD-2/3)
// ---------------------------------------------------------------------------
// Der Query laeuft nur fuer die, die verteilen duerfen. Das ist keine zweite
// Wahrheit neben dem Server-Gate, sondern dieselbe Funktion
// (`canDistributeLeads`) auf demselben Merkmal — sie verhindert nur, dass
// jemand bei jedem Startseiten-Aufruf einen 403 abholt. Verweigert wird
// serverseitig.
//
// LEAD-10: das Gate bleibt bewusst am DUERFEN und nicht am neuen
// `watchesUnassignedLeads`. Sandro darf zuteilen und muss darum laden — WAS er
// sieht (alles oder nur das Ueberfaellige), entscheidet der Server. Haenge man
// das Laden ans Sehen, bliebe seine Eskalation stumm.

export interface UnassignedLeadsView {
  readonly leads: readonly UnassignedLeadItem[];
  /** «alles» oder «nur das Ueberfaellige» — die Kachel titelt danach. */
  readonly scope: Exclude<LeadDistributionScope, "none">;
}

interface UnassignedLeadsResponse {
  ok: true;
  data: {
    leads: readonly UnassignedLeadItem[];
    generatedAt: string;
    scope: Exclude<LeadDistributionScope, "none">;
  };
}

export function useUnassignedLeads() {
  const { user } = useAuth();
  const mayDistribute = user ? canDistributeLeads(user) : false;
  return useQuery({
    queryKey: queryKeys.leads.unassigned(),
    enabled: mayDistribute,
    queryFn: async (): Promise<UnassignedLeadsView> => {
      const res = await apiGet<UnassignedLeadsResponse>(
        "/api/projects/unassigned-leads",
      );
      return { leads: res.data.leads, scope: res.data.scope };
    },
    // Wartezeit und Frist sind zeitabhaengig — eine Minute ist der Takt, in
    // dem sich die Anzeige noch ehrlich anfuehlt, ohne dass die Kachel bei
    // jedem Fokuswechsel neu laedt.
    staleTime: 60_000,
  });
}

interface AssignResponse {
  ok: true;
  data: { projectId: string; ownerUserId: string | null };
}

/**
 * Der eine Zuteil-Weg fuer unverteilte Leads
 * (`ProjectLeadService.assignOwner`). Wird von der Kachel UND von den beiden
 * Lead-Meldungszeilen benutzt — Sandro und Jacqueline klicken denselben Knopf
 * auf denselben Pfad.
 */
export function useAssignLeadOwner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      projectId: string;
      ownerUserId: string;
    }): Promise<AssignResponse["data"]> => {
      const res = await apiPost<AssignResponse>(
        `/api/projects/lead-assign?projectId=${encodeURIComponent(input.projectId)}`,
        { ownerUserId: input.ownerUserId },
      );
      return res.data;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: queryKeys.leads.all() });
      void qc.invalidateQueries({ queryKey: queryKeys.projects.all() });
      void qc.invalidateQueries({
        queryKey: queryKeys.projects.detail(data.projectId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.projects.events(data.projectId),
      });
      // Die Zuteilung laesst «Neue Anfrage» und «Lead unverteilt» verschwinden
      // und «Lead zugewiesen» beim PL entstehen.
      void qc.invalidateQueries({ queryKey: queryKeys.notifications.all() });
    },
  });
}
